import { useGetBeamerChatEnabled } from 'gcs-common/clients/api/entities/current-user/current-user-api-selectors';
import IconComponent from 'gcs-common/components/Icon/Icon';
import { BEAMER_CHANNEL_ID, BEAMER_CHANNEL_TITLE } from 'gcs-common/constants/channelConstants';
import { ICON, ICON_COLOR } from 'gcs-common/constants/IconConstants';
import { getSearchInput } from 'gcs-common/slices/channelList/channelListSelectors/getSearchInput';
import { getIsAgent } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { getFavicon } from 'gcs-common/slices/theme/themeSelectors';
import type { components } from 'gcs-common/types/openapi';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import ChannelBrandLogo from '../ChannelBrandLogo/ChannelBrandLogo';
import CheckBox from '../CheckBox/CheckBox';
import Highlight from '../Highlight/Highlight';
import ImageWithStatus from '../ImageWithStatus/ImageWithStatus';
import UnreadCountDisplay from '../UnreadCountDisplay/UnreadCountDisplay';
import styles from './styles.module.scss';

const getChannelStyle = ({
  isSelectedChannel,
  isAgent,
  isNewChannelOption,
  multiSelect,
}: {
  isSelectedChannel: boolean;
  isAgent: boolean;
  isNewChannelOption: boolean;
  multiSelect: boolean;
}) => {
  const mainStyle = isAgent ? styles.channelListItem : styles.craftsmanChannelListItem;
  let selectedStyle = isAgent ? styles.unselectedChannelListItem : styles.unselectedCraftsmanChannelListItem;
  if (isSelectedChannel) {
    selectedStyle = isAgent ? styles.selectedChannelListItem : styles.selectedCraftsmanChannelListItem;
  }

  const isNewChannelOptionStyle = isNewChannelOption ? styles.newChannel : '';
  const isAgentStyle = isAgent ? styles.agentStyle : '';
  const groupChannelSelectedStyle = multiSelect && isSelectedChannel ? styles.selectedGroupChannelListItem : '';

  return `${mainStyle} ${selectedStyle} ${isNewChannelOptionStyle} ${isAgentStyle} ${groupChannelSelectedStyle}`.trim();
};

interface ChannelListItemNormalProps {
  channelId?: string;
  isSalesSpace?: boolean;
  isSelectedChannel: boolean;
  unreadCount?: number;
  otherUserTyping?: boolean;
  channelMemberCount?: number;
  channelImage?: string;
  channelStatus: components['schemas']['ChatUserStatusDTO'];
  channelTitle: string;
  channelSubtitle: string;
  onChannelSelect: VoidFunction;
  multiSelect?: boolean;
  isNewChannelOption?: boolean;
  numOpenTasks?: number;
  channelBrand?: string;
  isHidden?: boolean;
}

const ChannelListItemNormal = (props: ChannelListItemNormalProps) => {
  const {
    channelId,
    isSelectedChannel,
    unreadCount = 0,
    otherUserTyping = false,
    channelImage = undefined,
    channelStatus,
    channelTitle,
    channelSubtitle,
    onChannelSelect = () => {},
    multiSelect = false,
    isNewChannelOption = false,
    numOpenTasks = 0,
    channelBrand = undefined,
    channelMemberCount = 0,
    isSalesSpace = false,
    isHidden = false,
  } = props;
  const isAgent = useSelector(getIsAgent);
  const logo = useSelector(getFavicon);

  const channelStyle = useMemo(() => {
    return getChannelStyle({
      isSelectedChannel,
      isAgent,
      isNewChannelOption,
      multiSelect,
    });
  }, [isAgent, isSelectedChannel, isNewChannelOption, multiSelect]);

  const searchInput = useSelector(getSearchInput);
  const isBeamerChatEnabled = useGetBeamerChatEnabled();

  if (channelId === BEAMER_CHANNEL_ID) {
    if (!isBeamerChatEnabled) return null;
    return (
      <button type="button" onClick={onChannelSelect} className={channelStyle}>
        <div className={styles.channelInfo}>
          <ImageWithStatus imageUrl={logo} isSalesSpace isAgent={false} className={styles.imageWrapper} />
          <div className={styles.content}>
            <div className={styles.customerTitle}>
              <div className={styles.flexContainer}>
                <div className={styles.customerName}>
                  <Highlight search={searchInput}>{BEAMER_CHANNEL_TITLE}</Highlight>
                </div>
              </div>
            </div>
          </div>
          {unreadCount > 0 && (
            <div className={styles.unreadMsgIndicator}>
              <UnreadCountDisplay unreadCount={unreadCount} />
            </div>
          )}
        </div>
      </button>
    );
  }

  return (
    <button type="button" onClick={onChannelSelect} className={channelStyle}>
      <div className={styles.channelInfo}>
        {!otherUserTyping && (
          <ImageWithStatus
            imageUrl={channelImage}
            userStatus={channelStatus}
            channelMemberCount={channelMemberCount}
            isSalesSpace={isSalesSpace}
            isAgent={isAgent}
            className={styles.imageWrapper}
          />
        )}
        {otherUserTyping && (
          <div className={styles.userTyping}>
            <IconComponent Icon={ICON.EDIT} alt="user-typing" />
          </div>
        )}
        <div className={styles.content}>
          <div className={styles.customerTitle}>
            <div className={styles.flexContainer}>
              <div className={styles.customerName}>
                <Highlight search={searchInput}>{channelTitle || 'Unbekannt'}</Highlight>
              </div>
              {isAgent && <div>{channelBrand && <ChannelBrandLogo channelBrand={channelBrand} />}</div>}
            </div>
          </div>
          <div className={styles.customerCompany}>
            <Highlight search={searchInput}>{channelSubtitle || 'Unbekannt'}</Highlight>
          </div>
          {isHidden && <div className={styles.archivedIndicator}>Archiviert</div>}
        </div>
        {unreadCount > 0 && (
          <div className={styles.unreadMsgIndicator}>
            <UnreadCountDisplay unreadCount={unreadCount} />
          </div>
        )}
        {numOpenTasks > 0 && <div className={styles.todoIndicator}>{numOpenTasks}</div>}
        {multiSelect && (
          <div className={styles.checkIconButton}>
            <CheckBox color={ICON_COLOR.NEW_CHAT_CHECKBOXES} isChecked={isSelectedChannel} onClick={() => {}} />
          </div>
        )}
        {isAgent && !multiSelect && (
          <div className={styles.chevronButton}>
            <IconComponent Icon={ICON.ARROW} alt="Weiter" />
          </div>
        )}
      </div>
    </button>
  );
};

export default ChannelListItemNormal;
